import "./assets/css/main.css";

import { createApp } from "vue";
import { createPinia } from "pinia";
import { createI18n } from "vue-i18n";
import VueKonva from "vue-konva";
import enUS from "@/i18n/en-US/index";
import aiemployeeEnUS from "@/modules/aiemployee/i18n/en-US";
import aiemployeeFr from "@/modules/aiemployee/i18n/fr";

// Boot files
import { initializeAuth } from "core-fe-lib/components-shadcn/auth/initializeAuth";
import { registerGlobalComponents } from "@/boot/register-components";

// IMPORTANT: modules/index MUST be imported before router.
// It calls registerModules() at evaluation time so that router/routes.ts
// finds a populated registry when it calls buildModuleRoutes(getModules(), ...).
import { modules } from "@/modules/index";

import App from "./App.vue";
import router from "./router";
import { applyTheme } from "core-fe-lib/components-shadcn/themes";
import { setDynamicFavicon } from "core-fe-lib/components-shadcn/favicon";
import { useUrl } from "core-fe-lib/composables/useUrl";
import { createUiServicesPlugin } from "core-fe-lib/plugins/ui-services-plugin";
import { useToast } from "core-fe-lib/components-shadcn/ui/toast/use-toast";
import { useDialog } from "core-fe-lib/components-shadcn/composables/useDialog";
import { mergeModuleMessages } from "core-fe-lib/components-shadcn/shell/i18n-factory";
import { setPlanLimitHandler } from "core-fe-lib/components-shadcn/composables/planLimit";
import { usePlanLimitStore } from "credit-fe-lib/stores/plan-limit";

// Initialize authentication (Kratos config + axios/OpenAPI) - MUST be before any API calls
initializeAuth();

// Initialize i18n — only load active locale eagerly; others loaded on demand
const i18n = createI18n({
  locale: "en-US",
  legacy: false,
  messages: { "en-US": enUS },
});

// Merge each module's i18n messages (care, scholar, etc.) into the i18n instance
mergeModuleMessages(i18n, modules);

// The aiemployee module was retired, but the LMS course-authoring assistant
// still reuses its ConversationView/ApprovalCard. Merge just that namespace so
// the embedded chat has its strings (the module itself is no longer registered).
i18n.global.mergeLocaleMessage("en-US", { aiemployee: aiemployeeEnUS });
i18n.global.mergeLocaleMessage("fr", { aiemployee: aiemployeeFr });

const app = createApp(App);

// Register global components (lightweight only)
registerGlobalComponents(app);

// Install plugins
app.use(createPinia());
app.use(router);
app.use(i18n);
// Registers <v-stage>, <v-layer>, <v-rect>, <v-line>, <v-transformer>, …
// for the slide module's Konva canvas (Phase 0.5 spike + Phase 3+).
app.use(VueKonva);
app.use(
  createUiServicesPlugin({
    toastHandler: useToast,
    dialogHandler: async (opts) => (await useDialog()).confirmDialog(opts),
  })
);

// Route 402 PLAN_LIMIT_REACHED errors (ADR 024) to the contextual upgrade
// dialog instead of a generic toast. The store is resolved lazily at event
// time, so Pinia is active.
setPlanLimitHandler((info) => usePlanLimitStore().openFor(info));

// Mount immediately for faster first paint.
// Tenant loading (theme/title/favicon) happens asynchronously after mount.
app.mount("#app");

// Load tenant configuration (async, non-blocking for first paint)
async function initializeTenantContext() {
  const { useTenantStore } = await import("core-fe-lib/stores/tenant-store");
  const { DefaultService } = await import("core-fe-lib/openapi/core");

  const tenantStore = useTenantStore();

  try {
    const tenant = await DefaultService.getPublicTenant();
    tenantStore.setTenant(tenant);

    const DARK = "dark";
    const initialTheme =
      localStorage.getItem("dark-mode") === "true" ? DARK : "light";
    const isDark = initialTheme === DARK;

    if (tenant.profile) {
      const colors = isDark
        ? tenant.profile.darkColors
        : tenant.profile.lightColors;
      applyTheme((colors || {}) as Record<string, string>);
    }

    let baseTitle = "CTO UP";
    const { isAdminSubdomain } = useUrl();
    if (!isAdminSubdomain(tenant.subdomain)) {
      baseTitle = tenant.subdomain;
    }
    document.title = baseTitle;
    window.__baseTitle = baseTitle;

    setDynamicFavicon(tenant.subdomain);
  } catch (error) {
    console.error("❌ Failed to load tenant:", error);
    setDynamicFavicon();
  }
}

void initializeTenantContext();

void router.isReady().then(() => {
  console.log("✅ Router ready");
});
